import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PlayerProvider } from './context/PlayerContext';
import { Toaster } from 'sonner';
import AudioPlayer from './components/AudioPlayer';

import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import About from './pages/About';
import Feed from './pages/Feed';
import Discover from './pages/Discover';
import Upload from './pages/Upload';
import Decks from './pages/Decks';
import LoopStation from './pages/LoopStation';
import BeatLab from './pages/BeatLab';
import AIStudio from './pages/AIStudio';
import ChalkFeed from './pages/ChalkFeed';
import LiveRoom from './pages/LiveRoom';
import LiveFeed from './pages/LiveFeed';
import TipSuccess from './pages/TipSuccess';
import TipCancel from './pages/TipCancel';
import Profile from './pages/Profile';
import Saved from './pages/Saved';
import { Playlists, PlaylistDetail } from './pages/Playlists';
import Messages from './pages/Messages';
import Logo from './components/Logo';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="flex flex-col items-center gap-6">
          <Logo size="md" />
          <div className="w-2 h-2 rounded-full bg-[#FFB000] animate-pulse" />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();

  // Handle OAuth callback from URL hash
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
        <Route path="/about" element={<About />} />
        <Route path="/dashboard" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
        <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
        <Route path="/upload" element={<ProtectedRoute><Upload /></ProtectedRoute>} />
        <Route path="/decks" element={<ProtectedRoute><Decks /></ProtectedRoute>} />
        <Route path="/loop-station" element={<ProtectedRoute><LoopStation /></ProtectedRoute>} />
        <Route path="/beat-lab" element={<ProtectedRoute><BeatLab /></ProtectedRoute>} />
        <Route path="/ai-studio" element={<ProtectedRoute><AIStudio /></ProtectedRoute>} />
        <Route path="/chalks" element={<ProtectedRoute><ChalkFeed /></ProtectedRoute>} />
        <Route path="/live/:streamId" element={<ProtectedRoute><LiveRoom /></ProtectedRoute>} />
        <Route path="/live" element={<ProtectedRoute><LiveFeed /></ProtectedRoute>} />
        <Route path="/tip/success" element={<ProtectedRoute><TipSuccess /></ProtectedRoute>} />
        <Route path="/tip/cancel" element={<ProtectedRoute><TipCancel /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/saved" element={<ProtectedRoute><Saved /></ProtectedRoute>} />
        <Route path="/playlists" element={<ProtectedRoute><Playlists /></ProtectedRoute>} />
        <Route path="/playlists/:playlistId" element={<ProtectedRoute><PlaylistDetail /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/messages/:userId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AudioPlayer />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PlayerProvider>
          <div className="App">
            <AppRouter />
            <Toaster
              position="top-right"
              theme="dark"
              toastOptions={{
                style: {
                  background: '#0F0F11',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontFamily: 'Manrope, sans-serif',
                },
              }}
            />
          </div>
        </PlayerProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
